import React, { useState, useEffect } from 'react';
import { getFuelRecords, getTeamSales } from '../../services/api';

const EmployeeDashboard = ({ user }) => {
  const [fuelRecords, setFuelRecords] = useState([]);
  const [allEmployeesRecords, setAllEmployeesRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState({ todays_sales: 0, points_today: 0 });

  // Detect if running on local or online server
  const isLocalServer = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

  // Helper function to get current date string based on environment
  const getCurrentDate = () => {
    const now = new Date();
    // For local server, use local timezone
    // For online server, you may need to adjust for server timezone
    return now.toISOString().split('T')[0]; // Returns 'YYYY-MM-DD'
  };

  // Helper function to extract date from record based on environment
  const getRecordDate = (record) => {
    if (!record.created_at) return null;
    
    if (isLocalServer) {
      // Local server: Extract date directly from string to avoid timezone conversion
      // Assumes format: 'YYYY-MM-DD HH:MM:SS'
      return record.created_at.split(' ')[0];
    } else {
      // Online server: Handle potential timezone differences
      // Try to parse the date and convert to local date string
      try {
        const recordDate = new Date(record.created_at);
        return recordDate.toISOString().split('T')[0];
      } catch (e) {
        // Fallback to string split if parsing fails
        return record.created_at.split(' ')[0];
      }
    }
  };

  // Helper function to filter records by current month
  const isCurrentMonth = (record) => {
    if (!record.created_at) return false;
    
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();
    
    if (isLocalServer) {
      // Local server: Parse date carefully to avoid timezone issues
      const recordDateStr = record.created_at.split(' ')[0]; // 'YYYY-MM-DD'
      const [year, month] = recordDateStr.split('-').map(Number);
      return year === currentYear && (month - 1) === currentMonth;
    } else {
      // Online server: Use Date object parsing
      try {
        const recordDate = new Date(record.created_at);
        return recordDate.getFullYear() === currentYear && 
               recordDate.getMonth() === currentMonth;
      } catch (e) {
        // Fallback to string parsing
        const recordDateStr = record.created_at.split(' ')[0];
        const [year, month] = recordDateStr.split('-').map(Number);
        return year === currentYear && (month - 1) === currentMonth;
      }
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const fuelResponse = await getFuelRecords();
        
        // Ensure data exists and is an array
        const records = Array.isArray(fuelResponse.data) ? fuelResponse.data : [];
        
        // Filter fuel records to show only current day for "My Sales Today"
        const today = getCurrentDate();
        
        const todayFuelRecords = records.filter(record => {
          const recordDate = getRecordDate(record);
          return recordDate === today;
        });
        
        setFuelRecords(todayFuelRecords);
        
        // Calculate metrics from fuel records
        const totalSales = todayFuelRecords.reduce((sum, record) => {
          return sum + (parseFloat(record.total_amount) || 0);
        }, 0);
        
        // Calculate points (1 point per 100 rupees)
        const pointsEarned = Math.floor(totalSales / 100);
        
        setMetrics({
          todays_sales: totalSales,
          points_today: pointsEarned
        });
        
        // Fetch all employees' fuel records for CURRENT MONTH (team leaderboard)
        // Backend now returns current month data directly
        try {
          const teamSalesResponse = await getTeamSales();
          const currentMonthRecords = teamSalesResponse.data || [];
          setAllEmployeesRecords(currentMonthRecords);
        } catch (err) {
          console.warn('Failed to fetch team sales', err);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        console.error('Error response:', error.response);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user.id]);

  // Greeting based on time of day
  const getGreeting = () => {
    const hr = new Date().getHours();
    if (hr < 12) return 'Good Morning';
    if (hr < 17) return 'Good Afternoon';
    return 'Good Evening';
  };

  // Refresh team sales data
  const refreshTeamSales = async () => {
    try {
      setLoading(true);
      const teamSalesResponse = await getTeamSales();
      // Backend now returns current month data directly
      const currentMonthRecords = teamSalesResponse.data || [];
      setAllEmployeesRecords(currentMonthRecords);
    } catch (err) {
      console.warn('Failed to refresh team sales', err);
    } finally {
      setLoading(false);
    }
  };

  const todaysSales = parseFloat(metrics.todays_sales) || 0;
  const pointsToday = parseFloat(metrics.points_today) || parseFloat(user.points_today) || parseFloat(user.points) || 0;

  return (
    <div className="max-w-7xl mx-auto py-3 px-3 sm:px-5 lg:px-7">
      <div className="py-3 sm:px-0">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">
              <span className="text-primary-600">{getGreeting()}</span>, {user.name}
            </h2>
          </div>
        </div>

        {/* Quick stats cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
          <div className="bg-white shadow rounded-lg p-4 flex flex-col">
            <div className="text-xs text-gray-500">Today's Sales</div>
            <div className="mt-2 text-2xl font-semibold text-gray-900">₹{todaysSales.toFixed(2)}</div>
          </div>
          <div className="bg-white shadow rounded-lg p-4 flex flex-col">
            <div className="text-xs text-gray-500">Points Earned Today</div>
            <div className="mt-2 text-2xl font-semibold text-gray-900">{pointsToday.toFixed(0)}</div>
          </div>
        </div>

        {/* Team Leaderboard - Grouped by Fuel Type */}
        <div className="bg-white shadow rounded-lg p-4 mb-4">
          <div className="flex justify-between items-center mb-3">
            <h3 className="text-lg font-medium text-gray-900">Team Leaderboard (This Month)</h3>
            <button 
              onClick={refreshTeamSales}
              className="text-sm text-primary-600 hover:text-primary-800"
            >
              Refresh
            </button>
          </div>
          {allEmployeesRecords.length > 0 ? (
            <div className="space-y-4">
              {/* Group sales by fuel type and employee */}
              {(() => {
                const fuelTypes = {};
                
                // Group records by fuel type and employee
                allEmployeesRecords.forEach(record => {
                  const fuelType = record.fuel_type || 'Other';
                  const employeeName = record.employee_name;
                  
                  if (!fuelTypes[fuelType]) {
                    fuelTypes[fuelType] = {};
                  }
                  
                  if (!fuelTypes[fuelType][employeeName]) {
                    fuelTypes[fuelType][employeeName] = { total: 0, count: 0, quantity: 0 };
                  }
                  
                  fuelTypes[fuelType][employeeName].total += parseFloat(record.total_amount || 0);
                  fuelTypes[fuelType][employeeName].count += 1;
                  fuelTypes[fuelType][employeeName].quantity += parseFloat(record.quantity || 0);
                });
                
                // Sort fuel types in preferred order
                const fuelOrder = ['Petrol', 'Diesel', 'Power', 'Oil', 'Additive'];
                const sortedFuelTypes = Object.keys(fuelTypes).sort((a, b) => {
                  const aIndex = fuelOrder.indexOf(a);
                  const bIndex = fuelOrder.indexOf(b);
                  if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
                  if (aIndex !== -1) return -1;
                  if (bIndex !== -1) return 1;
                  return a.localeCompare(b);
                });
                
                return sortedFuelTypes.map((fuelType) => {
                  // Sort employees by total sales within each fuel type
                  const employees = Object.entries(fuelTypes[fuelType])
                    .sort((a, b) => b[1].total - a[1].total)
                    .map(([name, data], index) => ({ name, ...data, rank: index + 1 }));
                  
                  // Calculate total for this fuel type
                  const fuelTotal = employees.reduce((sum, emp) => sum + emp.total, 0);
                  
                  return (
                    <div key={fuelType} className="border border-gray-300 rounded-lg overflow-hidden">
                      {/* Fuel Type Header */}
                      <div className="bg-gradient-to-r from-primary-500 to-primary-600 px-4 py-3">
                        <div className="flex justify-between items-center">
                          <h4 className="font-semibold text-white text-base">{fuelType}</h4>
                          <div className="text-white text-sm font-medium">Total: ₹{fuelTotal.toFixed(2)}</div>
                        </div>
                      </div>
                      
                      {/* Employee List for this Fuel Type */}
                      <div className="bg-gray-50 p-3 space-y-2">
                        {employees.map((emp) => (
                          <div 
                            key={`${fuelType}-${emp.name}`}
                            className={`border rounded-lg p-3 flex items-center justify-between ${
                              emp.name === user.name 
                                ? 'bg-primary-50 border-primary-400 shadow-sm' 
                                : 'bg-white border-gray-200 hover:bg-gray-50'
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                                emp.rank === 1 ? 'bg-yellow-100 text-yellow-700' :
                                emp.rank === 2 ? 'bg-gray-200 text-gray-700' :
                                emp.rank === 3 ? 'bg-orange-100 text-orange-700' :
                                'bg-gray-100 text-gray-600'
                              }`}>
                                {emp.rank}
                              </div>
                              <div>
                                <div className="font-medium text-gray-900">
                                  {emp.name}
                                  {emp.name === user.name && (
                                    <span className="ml-2 text-xs text-primary-600 font-semibold">(You)</span>
                                  )}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {emp.quantity.toFixed(2)} L • {emp.count} {emp.count === 1 ? 'sale' : 'sales'}
                                </div>
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="font-semibold text-gray-900">₹{emp.total.toFixed(2)}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                });
              })()}
            </div>
          ) : (
            <div className="text-center py-4 text-gray-500">No team sales recorded this month</div>
          )}
        </div>

        {/* My Sales Today */}
        <div className="bg-white shadow rounded-lg p-4 mb-4">
          <h3 className="text-lg font-medium text-gray-900 mb-3">My Sales Today</h3>
          {fuelRecords.length > 0 ? (
            <div className="space-y-3">
              {fuelRecords.map((record, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-3 hover:bg-gray-50">
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="font-medium text-gray-900">{record.fuel_type}</div>
                      <div className="text-sm text-gray-500">
                        {record.quantity} L
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-medium text-gray-900">₹{record.total_amount !== undefined && record.total_amount !== null && !isNaN(record.total_amount) ? parseFloat(record.total_amount).toFixed(2) : 'N/A'}</div>
                      <div className="text-xs text-gray-500">
                        {new Date(record.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-4 text-gray-500">No sales recorded today</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmployeeDashboard;